import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from './AuthContext';
import { panelKeyForPath } from '../config/accessPanels';
import { getMyPermissions } from '../hrms/api/access';

type PanelLevel = 'view' | 'manage';
interface PanelPermission {
  visible: boolean;
  level: PanelLevel;
}

interface AccessClaim {
  hasAccess: boolean;
  permissions: Record<string, PanelPermission>;
}

interface AccessContextValue extends AccessClaim {
  loading: boolean;
  isPanelVisible: (key: string) => boolean;
  isPathVisible: (pathname: string) => boolean;
  canManage: (key: string) => boolean;
  refresh: () => void;
}

// Read the Access profile straight from the JWT (TeamLio embeds it as the
// `access` claim at login; messenger logs in via TeamLio's /api/auth/login-select
// so the token carries it). No API call. Absent/invalid claim → permissive.
function readAccessFromToken(): AccessClaim {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) return { hasAccess: false, permissions: {} };
    const decoded: any = jwtDecode(token);
    const a = decoded?.access;
    if (a && a.has_access) {
      return {
        hasAccess: true,
        permissions: (a.permissions as Record<string, PanelPermission>) || {},
      };
    }
  } catch {
    /* malformed token → permissive */
  }
  return { hasAccess: false, permissions: {} };
}

const DEFAULT: AccessContextValue = {
  loading: false,
  hasAccess: false,
  permissions: {},
  isPanelVisible: () => true,
  isPathVisible: () => true,
  canManage: () => true,
  refresh: () => {},
};

const AccessContext = createContext<AccessContextValue>(DEFAULT);

export const useAccess = () => useContext(AccessContext);

// What a key that a saved profile predates falls back to. Mirrors TeamLio's
// panelCatalog (`defaultVisible` / `defaultLevel`) so both apps agree: a profile
// that simply never listed the key must not be treated as an explicit "no".
const ABSENT_KEY_DEFAULTS: Record<string, PanelPermission> = {
  expenses: { visible: true, level: 'view' },
  dbms: { visible: true, level: 'manage' },
  notemaker: { visible: true, level: 'manage' },
  // Leaves sub-tabs — each is a leveled feature under the "leaves" panel.
  'leaves-my': { visible: true, level: 'manage' },
  'leaves-approvals': { visible: false, level: 'manage' },
  'leaves-team': { visible: false, level: 'view' },
  'leaves-holidays': { visible: true, level: 'view' },
};

// Expenses was introduced after some Access profiles had already been saved.
// Treat an omitted expense key as the legacy default (visible), but honor an
// explicit Access Management choice as soon as the key is present in the JWT.
export function isPanelVisibleForAccessClaim(
  hasAccess: boolean,
  permissions: Record<string, PanelPermission>,
  key: string,
): boolean {
  if (!hasAccess) return true;
  const permission = permissions[key];
  if (permission === undefined) return ABSENT_KEY_DEFAULTS[key]?.visible === true;
  return permission.visible === true;
}

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [claim, setClaim] = useState<AccessClaim>(() => readAccessFromToken());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    setClaim(readAccessFromToken());
    if (!localStorage.getItem('accessToken')) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getMyPermissions()
      .then((permissions) => {
        setClaim({
          hasAccess: permissions.has_access === true,
          permissions: permissions.permissions || {},
        });
      })
      .catch(() => {
        // Keep the token-derived permissions if the live Teamlio lookup fails.
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [isAuthenticated, refresh]);

  const value = useMemo<AccessContextValue>(() => {
    const { hasAccess, permissions } = claim;
    const isPanelVisible = (key: string) =>
      isPanelVisibleForAccessClaim(hasAccess, permissions, key);
    const isPathVisible = (pathname: string) => {
      const key = panelKeyForPath(pathname);
      if (!key) return true; // native page (chat/calls/calendar)
      return isPanelVisible(key);
    };
    const canManage = (key: string) => {
      if (!hasAccess) return true;
      const permission = permissions[key];
      // Profile saved before this panel existed → use its declared default
      // instead of silently denying, the same way isPanelVisible does.
      if (permission === undefined) {
        const fallback = ABSENT_KEY_DEFAULTS[key];
        return fallback?.visible === true && fallback.level === 'manage';
      }
      return permission.visible === true && permission.level === 'manage';
    };
    return { loading, ...claim, isPanelVisible, isPathVisible, canManage, refresh };
  }, [claim, loading, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
